import { createApi } from '@reduxjs/toolkit/query/react';

import { setError } from './configurationSlice';
import { IBusIsLateConfiguration } from '../types/ISettings';

import {
  MESSAGES,
  SCOPES,
  createNotificationHandler,
} from '@/redux/notificationHandler';
import { axiosBaseQuery } from '@/redux/axios';
import { apiRoutes } from '@/api/apiRoutes';

export const busIsLateConfigurationsApi = createApi({
  reducerPath: 'busIsLateApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['BusIsLateConfigurations'],
  endpoints: (builder) => ({
    getBusIsLateConfigurations: builder.query<IBusIsLateConfiguration[], void>({
      query: () => ({
        url: apiRoutes.getBusIsLateConfigurations.path(),
        method: apiRoutes.getBusIsLateConfigurations.method,
      }),
      providesTags: ['BusIsLateConfigurations'],
      onQueryStarted: createNotificationHandler(
        setError,
        SCOPES.SETTINGS_BUS_IS_LATE,
        MESSAGES.LOAD,
        false
      ),
    }),
    createBusIsLateConfiguration: builder.mutation<
      IBusIsLateConfiguration,
      Partial<IBusIsLateConfiguration>
    >({
      query: (configuration) => ({
        url: apiRoutes.createBusIsLateConfiguration.path(),
        method: apiRoutes.createBusIsLateConfiguration.method,
        data: configuration,
      }),
      async onQueryStarted(configuration, { dispatch, queryFulfilled }) {
        createNotificationHandler(
          setError,
          SCOPES.SETTINGS_BUS_IS_LATE,
          MESSAGES.ADD
        )(configuration, { dispatch, queryFulfilled });

        const { data: newItem } = await queryFulfilled;
        dispatch(
          busIsLateConfigurationsApi.util.updateQueryData(
            'getBusIsLateConfigurations',
            undefined,
            (draft) => {
              draft.push(newItem);
            }
          )
        );
      },
    }),
    updateBusIsLateConfiguration: builder.mutation<
      IBusIsLateConfiguration,
      IBusIsLateConfiguration
    >({
      query: (configuration) => ({
        url: apiRoutes.updateBusIsLateConfigurationById.path(
          configuration.id as number
        ),
        method: apiRoutes.updateBusIsLateConfigurationById.method,
        data: configuration,
      }),
      async onQueryStarted(configuration, { dispatch, queryFulfilled }) {
        createNotificationHandler(
          setError,
          SCOPES.SETTINGS_BUS_IS_LATE,
          MESSAGES.UPDATE
        )(configuration, { dispatch, queryFulfilled });

        const { data: updatedItem } = await queryFulfilled;
        dispatch(
          busIsLateConfigurationsApi.util.updateQueryData(
            'getBusIsLateConfigurations',
            undefined,
            (draft) => {
              const idx = draft.findIndex((item) => item.id === updatedItem.id);
              if (idx !== -1) {
                draft[idx] = { ...draft[idx], ...updatedItem };
              }
            }
          )
        );
      },
    }),
    removeBusIsLateConfiguration: builder.mutation<
      { success: boolean },
      { id: string | number }
    >({
      query: ({ id }) => ({
        url: apiRoutes.deleteBusIsLateConfigurationById.path(id as number),
        method: apiRoutes.deleteBusIsLateConfigurationById.method,
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        createNotificationHandler(
          setError,
          SCOPES.SETTINGS_BUS_IS_LATE,
          MESSAGES.DELETE
        )({ id }, { dispatch, queryFulfilled });

        await queryFulfilled;
        const numericId = typeof id === 'string' ? Number(id) : id;
        dispatch(
          busIsLateConfigurationsApi.util.updateQueryData(
            'getBusIsLateConfigurations',
            undefined,
            (draft) => {
              const idx = draft.findIndex((item) => item.id === numericId);
              if (idx !== -1) {
                draft.splice(idx, 1);
              }
            }
          )
        );
      },
    }),
  }),
});

export const {
  useGetBusIsLateConfigurationsQuery,
  useCreateBusIsLateConfigurationMutation,
  useUpdateBusIsLateConfigurationMutation,
  useRemoveBusIsLateConfigurationMutation,
} = busIsLateConfigurationsApi;

export default busIsLateConfigurationsApi;
